import { useEffect } from "react";
import { navigate } from "@reach/router";
import { useSelector } from "react-redux";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { selectCurrentUserId } from "newui/features/Auth/authSelectors";
import { useHasAIEvaluationAccess } from "hooks/featureFlags";
import { useEvaluation } from "../../hooks/useEvaluation";
import { InitialEvaluationProvider } from "../../hooks/InitialEvaluationContext";
import { EVALUATION_STEP_NUMBERS } from "../../constants/evaluation.constants";
import { RfxLeadInfoBox } from "../steps/InitialEvaluationStep/panel/RfxLeadInfoBox";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import type { PanelMode } from "../../types/panel.type";
import { InvitationBanner } from "../steps/InitialEvaluationStep/panel";
import { EvaluationModeSelector } from "./EvaluationModeSelector";
import { EvaluationAccessBoundary } from "../EvaluationAccessBoundary";

interface EvaluationTabDisplayProps {
	isClientView: boolean;
	rfxData: Rfx;
	rfxSubmissionId?: number;
}

export const EvaluationTabDisplay: React.FC<EvaluationTabDisplayProps> = ({
	rfxData,
	rfxSubmissionId,
	isClientView,
}) => {
	const { clientPaths } = useAuthContext();
	const hasAIAccess = useHasAIEvaluationAccess();
	const currentUserId = useSelector(selectCurrentUserId);
	const rfxId = rfxData?.id;
	const rfxLead = rfxData?.rfxLead;

	const {
		hasStarted,
		isLoading,
		evaluationState,
		currentUserContext,
		accessError,
		fetchEvaluationState,
		startEvaluation,
		changeMode,
	} = useEvaluation({ rfxId, rfxSubmissionId });

	// Pre-creation: compare current user with rfxLead.id
	// Post-creation: use the backend-derived context
	const isRfxLead = evaluationState
		? (currentUserContext?.isRfxLead ?? false)
		: (currentUserId !== null && rfxLead?.id === currentUserId);

	// A member who declared a conflict of interest is blocked from scoring until
	// the RFx Lead reviews it. The /status endpoint still loads, so we explain the
	// hold here rather than showing the generic "not yet started" copy.
	const conflictPendingReview =
		!isRfxLead &&
		currentUserContext?.scoringBlockedReason === "conflict_pending_review";

	useEffect(() => {
		fetchEvaluationState();
	}, [fetchEvaluationState]);

	const handleView = (): void => {
		const evaluationId = evaluationState?.id;
		if (evaluationId) {
			navigate(clientPaths.viewEvaluation(rfxId, evaluationId));
		}
	};

	const handleCreate = async (panelMode: PanelMode): Promise<void> => {
		if (!isRfxLead) return;
		const state = await startEvaluation(panelMode);
		const evaluationId = state?.id;
		if (evaluationId) {
			navigate(clientPaths.viewEvaluation(rfxId, evaluationId));
		}
	};

	// Compute whether mode switching is currently allowed
	const canChangeMode = (() => {
		if (!hasStarted || !evaluationState?.panelMode) return false;
		const mode = evaluationState.panelMode;
		const step = evaluationState.currentStep ?? 0;
		// Solo → Panel: allowed before Final Scoring (step number 5)
		if (mode === "manual_solo" || mode === "ai_solo") {
			return step < EVALUATION_STEP_NUMBERS.PANEL_MEETING;
		}
		// Panel → Solo: allowed before invites sent (for now, pass true; refine after testing)
		if (mode === "manual_panel" || mode === "ai_panel") {
			return true;
		}
		return false;
	})();

	const handleModeChange = async (newMode: PanelMode): Promise<void> => {
		await changeMode(newMode);
	};

	const handleAcceptPanelInvite = () => {
		navigate(clientPaths.viewEvaluation(rfxId, evaluationState?.id));
	};

	const handleDeclined = () => {
		fetchEvaluationState();
	};

	if (!isClientView) {
		return null;
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="60%"
					height="40px"
					className="!rounded-lg"
				/>
			</div>
		);
	}

	const hasDeclined = currentUserContext?.panelMemberStatus === "declined";

	// A panel member who is blocked from this evaluation (revoked, or a declared
	// conflict of interest awaiting RFx Lead review) gets a 403 on the state
	// endpoint. Mirror the dedicated evaluation page and show the Access Denied
	// screen rather than falling through to the generic "not yet started" copy.
	return (
		<EvaluationAccessBoundary error={accessError} rfxLead={rfxLead}>
			<InitialEvaluationProvider rfxId={rfxId}>
				<div data-testid="evaluation-panel" className="min-h-96">
					<div className="mb-6">
						<h2 className="text-xl font-semibold text-gray-900">
							RFx Evaluation Process
						</h2>
						<p className="mt-1 text-sm text-gray-500">
							{hasAIAccess
								? "Use AI-assisted evaluation to systematically score and compare supplier submissions."
								: "Systematically score and compare supplier submissions."}
						</p>
					</div>
					{hasStarted && !isRfxLead && (
						<div className="mb-4">
							<InvitationBanner evalId={evaluationState?.id} memberId={currentUserContext?.panelMemberId} rfxData={rfxData}
								onAccept={handleAcceptPanelInvite} currentStatus={currentUserContext?.panelMemberStatus} currentStep={evaluationState?.currentStep} onDecline={handleDeclined}
								disclaimerRequired={currentUserContext?.disclaimerRequired}
								isConflictPending={conflictPendingReview} />
						</div>
					)}
					{isRfxLead ? (
						<div>
							{hasStarted && (
								<Button
									btntype="primary"
									onClick={handleView}
									data-testid="view-evaluation-btn"
									className="mb-4"
								>
									View Evaluation
								</Button>
							)}
							<EvaluationModeSelector
								onStart={handleCreate}
								hasAIAccess={hasAIAccess}
								isLoading={isLoading}
								currentMode={evaluationState?.panelMode as PanelMode | undefined}
								currentStep={evaluationState?.currentStep}
								canChangeMode={canChangeMode}
								onModeChange={handleModeChange}
							/>
						</div>
					) : !hasStarted ? (
						<div data-testid="evaluation-not-started-info">
							<InstructionsInfoBox
								title="Evaluation not yet started"
								collapsible={false}
								instructions={[
									"Only the RFx Lead can start the evaluation process for this RFx.",
									"Once started, you will be notified if you are invited to participate as a panel member.",
									"Please contact the RFx Lead below if you have any questions.",
								]}
							/>
							<RfxLeadInfoBox
								rfxLead={rfxLead}
								marginTop="4"
							/>
						</div>
					) : !conflictPendingReview && !hasDeclined && (
						<div>
							<Button
								btntype="primary"
								onClick={handleView}
								data-testid="view-evaluation-btn"
								className="mb-4"
							>
								View Evaluation
							</Button>
							<EvaluationModeSelector
								onStart={handleCreate}
								hasAIAccess={hasAIAccess}
								isLoading={false}
								currentMode={evaluationState?.panelMode as PanelMode | undefined}
								currentStep={evaluationState?.currentStep}
								canChangeMode={false}
							/>
						</div>
					)}
				</div>
			</InitialEvaluationProvider>
		</EvaluationAccessBoundary>
	);
};
